<template>
    <div>
        <div v-if="data.contents.length" class="bg-white border rounded mb-2">
            <draggable
                v-model="data.contents"
                handle=".step-handle"
                item-key="unique_id"
                :animation="150"
                class="divide-y"
            >
                <template #item="{ element: step, index }">
                    <div class="p-3 space-y-2">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <button type="button" class="step-handle cursor-move focus:outline-none">
                                    <svg class="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path></svg>
                                </button>
                                <span class="text-sm">Step {{ index + 1 }}</span>
                            </div>
                            <btn type="light" size="xs" @click="removeContent(step.unique_id)">
                                <icon name="delete" size="14" class="mr-1" />
                                Delete
                            </btn>
                        </div>

                        <div v-if="data.display_mode === 'icons'" class="flex items-center gap-1 overflow-x-auto">
                            <button
                                v-for="ic in availableIcons"
                                :key="ic.id"
                                type="button"
                                class="p-1.5 rounded border transition-colors"
                                :class="step.icon === ic.id ? 'border-gray-900 bg-gray-50' : 'border-transparent hover:border-gray-300'"
                                :title="ic.label"
                                @click="step.icon = ic.id"
                                v-html="ic.svg"
                            >
                            </button>
                        </div>

                        <div class="grid grid-cols-2 gap-2">
                            <text-field
                                v-model="step.title"
                                placeholder="Step title"
                            />
                            <text-field
                                v-model="step.description"
                                placeholder="Step description"
                            />
                        </div>
                    </div>
                </template>
            </draggable>
        </div>

        <div class="flex items-center justify-between">
            <btn v-if="data.contents.length < 5" type="secondary" size="small" icon-name="add-circle" @click="addContent">Add step</btn>
            <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500">Display</span>
                <select
                    v-model="data.display_mode"
                    class="border border-gray-300 rounded text-sm py-1 px-2 focus:ring-0 focus:border-gray-400"
                >
                    <option value="numbers">Numbers</option>
                    <option value="icons">Icons</option>
                </select>
            </div>
        </div>
    </div>
</template>

<script setup>
import {defineProps} from 'vue';
import draggable from 'vuedraggable';
import {uniqueId} from '../helpers';

const availableIcons = [
    { id: 'user-plus', label: 'Join', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>' },
    { id: 'share', label: 'Share', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>' },
    { id: 'dollar', label: 'Earn', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>' },
    { id: 'gift', label: 'Gift', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>' },
    { id: 'star', label: 'Star', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>' },
    { id: 'check-circle', label: 'Complete', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>' },
    { id: 'megaphone', label: 'Announce', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>' },
    { id: 'trending-up', label: 'Growth', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>' },
    { id: 'mail', label: 'Email', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>' },
    { id: 'rocket', label: 'Launch', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>' },
    { id: 'heart', label: 'Love', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>' },
    { id: 'link', label: 'Link', svg: '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>' },
];

const props = defineProps({
    data: Object
});

if (!props.data.display_mode) {
    props.data.display_mode = 'numbers';
}

if (!props.data.contents || !props.data.contents.length) {
    props.data.display_mode = 'icons';
    props.data.contents = [
        {
            unique_id: uniqueId(),
            title: 'Join the program',
            description: 'Sign up and get your unique referral link to share.',
            icon: 'user-plus',
        },
        {
            unique_id: uniqueId(),
            title: 'Share your link',
            description: 'Spread the word on social media, your website, or directly to your audience.',
            icon: 'share',
        },
        {
            unique_id: uniqueId(),
            title: 'Earn commissions',
            description: 'Get rewarded for every successful referral you make.',
            icon: 'dollar',
        },
    ];
}

const addContent = () => {
    props.data.contents.push({
        unique_id: uniqueId(),
        title: '',
        description: '',
        icon: 'star',
    });
}

const removeContent = (id) => {
    const index = props.data.contents.findIndex(content => content.unique_id === id);
    if (index !== -1) {
        props.data.contents.splice(index, 1);
    }
}
</script>
